/**
 * Fullscreen template editor (/vorlagen/:id/bearbeiten).
 *
 * Layout:
 *   ┌─────────── Topbar (sticky, white) ─────────────┐
 *   │  ←Back  Name  ·  Save · Versionen · PDF · DOCX │
 *   ├────────── Toolbar (sticky) ────────────────────┤
 *   ├────────────┬─────────────────────┬─────────────┤
 *   │  Settings  │  A4 Page (scroll)   │  Macros     │
 *   │  (210px)   │   max-width 794px   │  (320px)    │
 *   └────────────┴─────────────────────┴─────────────┘
 *
 * No outer page scroll – topbar/toolbar fixed, only A4 + macro panel scroll.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";

import { buildEditorExtensions } from "@/components/editor/extensions";
import WordRibbon from "@/components/editor/WordRibbon";
import CategorySelect from "@/components/editor/CategorySelect";
import SnippetPicker from "@/components/editor/SnippetPicker";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-font-family";
import { Highlight } from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";

import api, { formatApiError, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CaretLeft, FloppyDisk, FilePdf, FileDoc, Eye, EyeSlash,
  ClockCounterClockwise, MagnifyingGlass, Sparkle,
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  ListBullets, ListNumbers, TextAlignLeft, TextAlignCenter,
  TextAlignRight, TextAlignJustify, ArrowUUpLeft, ArrowUUpRight,
  Link as LinkIcon, TextHOne, TextHTwo, TextHThree, Minus,
  Table as TableIcon, X, ArrowCounterClockwise, Highlighter, MagicWand, Stack,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const SECTION_LABEL = {
  letter: "Brief", sender: "Absender", account: "Bankverbindung",
  recipient: "Empfänger", property: "Objekt", tenancy: "Mietverhältnis",
  date: "Datum & Fristen", nk: "Nebenkosten", snippet: "Bausteine (Snippets)",
  ratenzahlung: "Ratenzahlung", vorgang: "Vorgang", unit: "Einheit", mieterhoehung: "Mieterhöhung",
};

const FONT_COLORS = ["#0f172a", "#475569", "#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899"];
const HIGHLIGHT_COLORS = ["#fef08a", "#bbf7d0", "#bfdbfe", "#fecaca", "#fce7f3", "#e9d5ff"];

const AUTOSAVE_DEBOUNCE_MS = 1500;

export default function DocumentTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tpl, setTpl] = useState(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Allgemein");
  const [subject, setSubject] = useState("");
  const [subSubject, setSubSubject] = useState("");
  const [description, setDescription] = useState("");
  const [body, setBody] = useState("<p></p>");
  // Phase 21β-2.4 – Layout/Design
  const [useLetterhead, setUseLetterhead] = useState(true);
  const [fontFamily, setFontFamily] = useState("Calibri");
  const [fontSizePt, setFontSizePt] = useState(11);
  const [lineHeight, setLineHeight] = useState(1.5);
  const [marginTop, setMarginTop] = useState(25);
  const [marginBottom, setMarginBottom] = useState(25);
  const [marginLeft, setMarginLeft] = useState(25);
  const [marginRight, setMarginRight] = useState(25);
  // Phase 21β-2.7 — Word-Ribbon Layout-Tab fields
  const [pageOrientation, setPageOrientation] = useState("portrait");
  const [pageFormat, setPageFormat] = useState("a4");
  const [pageColumns, setPageColumns] = useState(1);
  const [hyphenation, setHyphenation] = useState(false);

  const [macros, setMacros] = useState({});
  const [macroSearch, setMacroSearch] = useState("");
  const [tenancies, setTenancies] = useState([]);
  const [previewTenancyId, setPreviewTenancyId] = useState("");
  const [billings, setBillings] = useState([]);
  const [previewBillingId, setPreviewBillingId] = useState("");

  const [livePreview, setLivePreview] = useState(false);
  const [previewedHtml, setPreviewedHtml] = useState(null);
  const [previewedSubject, setPreviewedSubject] = useState("");

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [aiImproveOpen, setAiImproveOpen] = useState(false);
  const [snippetPickerOpen, setSnippetPickerOpen] = useState(false);

  const editorReadyRef = useRef(false);
  const saveTimerRef = useRef(null);

  // ---- Editor ----
  const editor = useEditor({
    extensions: buildEditorExtensions({
      placeholder: "Beginne mit deinem Text … nutze /Platzhalter aus dem Panel rechts.",
    }),
    content: "<p></p>",
    editorProps: {
      attributes: {
        // tiptap-doc class is styled in index.css with all formatting rules
        class: "tiptap-doc",
      },
    },
    onUpdate: ({ editor }) => {
      if (!editorReadyRef.current) return;
      setBody(editor.getHTML());
      setDirty(true);
    },
  });

  // Phase 21β-3.2 — Editierbarer Vorschau-Editor (zweite TipTap-Instanz).
  // Edits in der Vorschau gehen nicht in die Template zurück (sonst gingen
  // die Platzhalter verloren) — sie werden temporär gehalten und auf Wunsch
  // direkt als PDF/DOCX exportiert (siehe "exportPreview" unten).
  const [previewDirty, setPreviewDirty] = useState(false);
  const previewReadyRef = useRef(false);
  const previewEditor = useEditor({
    extensions: buildEditorExtensions({
      placeholder: "Vorschau wird geladen …",
    }),
    content: "<p></p>",
    editorProps: {
      attributes: { class: "tiptap-doc" },
    },
    onUpdate: ({ editor: e }) => {
      if (!previewReadyRef.current) return;
      setPreviewedHtml(e.getHTML());
      setPreviewDirty(true);
    },
  });

  // ---- Load template + macros ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: t }, { data: m }, { data: ts }, billingsRes] = await Promise.all([
          api.get(`/templates/${id}`),
          api.get("/templates/macros"),
          api.get("/tenancies").catch(() => ({ data: [] })),
          api.get("/nk-billings").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setTpl(t);
        setName(t.name || "");
        setCategory(t.category || "Allgemein");
        setSubject(t.subject || "");
        // 2026-02 (feb) — Unterbetreff mit sinnvollem Default vorbelegen,
        // wenn Vorlage keinen eigenen Wert hat.
        setSubSubject(
          t.sub_subject !== undefined && t.sub_subject !== null && t.sub_subject !== ""
            ? t.sub_subject
            : "Ihr Mietverhältnis: {{tenancy.contract_number}}",
        );
        setDescription(t.description || "");
        setBody(t.body || "<p></p>");
        setUseLetterhead(t.use_letterhead !== false);
        // Phase 21β-4 — User-Voreinstellungen anwenden, wenn die Vorlage noch
        // keine eigene Schriftart/-größe gesetzt hat (gilt nur für „neue Dokumente").
        let userPref = { family: "", size: null };
        try {
          const { data: me } = await api.get("/users/me");
          userPref = {
            family: me?.editor_font_family || "",
            size: me?.editor_font_size ? Number(me.editor_font_size) : null,
          };
        } catch { /* user prefs are optional */ }
        setFontFamily(t.font_family || userPref.family || "Calibri");
        setFontSizePt(Number(t.font_size_pt) || userPref.size || 11);
        setLineHeight(Number(t.line_height) || 1.5);
        setMarginTop(Number(t.page_margin_top_mm) || 25);
        setMarginBottom(Number(t.page_margin_bottom_mm) || 25);
        setMarginLeft(Number(t.page_margin_left_mm) || 25);
        setMarginRight(Number(t.page_margin_right_mm) || 25);
        setPageOrientation(t.page_orientation || "portrait");
        setPageFormat(t.page_format || "a4");
        setPageColumns(Number(t.page_columns) || 1);
        setHyphenation(!!t.hyphenation);
        setMacros(m || {});
        const ten = Array.isArray(ts) ? ts : (ts?.items || []);
        setTenancies(ten.slice(0, 200));
        if (ten.length) setPreviewTenancyId(ten[0].id);
        const bls = Array.isArray(billingsRes.data) ? billingsRes.data : [];
        setBillings(bls);
        if (editor && !cancelled) {
          editor.commands.setContent(t.body || "<p></p>", false);
          setTimeout(() => { editorReadyRef.current = true; }, 50);
        }
        setDirty(false);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/vorlagen");
      }
    })();
    return () => { cancelled = true; editorReadyRef.current = false; };
  }, [id, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Debounced autosave ----
  const performSave = useCallback(async (silent = false) => {
    if (!name?.trim() || !subject?.trim()) {
      if (!silent) toast.error("Bitte Name und Betreff angeben.");
      return false;
    }
    setSaving(true);
    try {
      const payload = {
        name, category: category || "Allgemein", subject,
        sub_subject: subSubject,
        body: editor?.getHTML() || body, description, is_html: true,
        use_letterhead: useLetterhead,
        font_family: fontFamily || null,
        font_size_pt: Number(fontSizePt) || null,
        line_height: Number(lineHeight) || null,
        page_margin_top_mm: Number(marginTop) || null,
        page_margin_bottom_mm: Number(marginBottom) || null,
        page_margin_left_mm: Number(marginLeft) || null,
        page_margin_right_mm: Number(marginRight) || null,
        page_orientation: pageOrientation || null,
        page_format: pageFormat || null,
        page_columns: Number(pageColumns) || null,
        hyphenation: !!hyphenation,
      };
      await api.patch(`/templates/${id}`, payload);
      setSavedAt(new Date());
      setDirty(false);
      if (!silent) toast.success("Vorlage gespeichert");
      return true;
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [id, name, category, subject, subSubject, description, body, editor,
       useLetterhead, fontFamily, fontSizePt, lineHeight,
       marginTop, marginBottom, marginLeft, marginRight,
       pageOrientation, pageFormat, pageColumns, hyphenation]);

  useEffect(() => {
    if (!dirty) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      performSave(true);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [dirty, name, subject, subSubject, body, description, category,
       useLetterhead, fontFamily, fontSizePt, lineHeight,
       marginTop, marginBottom, marginLeft, marginRight, performSave]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // ---- Live preview render ----
  // We always render the subject for the side-panel preview, even when
  // livePreview body is off — the user wants to see the resolved subject.
  const runPreview = useCallback(async () => {
    try {
      const { data } = await api.post(`/templates/${id}/render?format=html`, {
        tenancy_id: previewTenancyId || undefined,
        nk_billing_id: previewBillingId || undefined,
        override_body: editor?.getHTML() || body,
        override_subject: subject,
      });
      setPreviewedSubject(data.subject);
      setPreviewedHtml(livePreview ? data.body : null);
      // Wenn Vorschau aktiv: bearbeitbares Vorschau-Doc neu befüllen
      if (livePreview && previewEditor) {
        previewReadyRef.current = false;
        previewEditor.commands.setContent(data.body || "<p></p>", false);
        setPreviewDirty(false);
        setTimeout(() => { previewReadyRef.current = true; }, 30);
      }
    } catch {
      // Failure here is non-blocking; subject preview just stays hidden.
      setPreviewedHtml(livePreview ? null : null);
    }
  }, [id, livePreview, previewTenancyId, previewBillingId, subject, body, editor, previewEditor]);

  useEffect(() => { runPreview(); }, [livePreview, previewTenancyId, previewBillingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-render preview on body/subject change (debounced 600ms; always for subject).
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => { runPreview(); }, 600);
    return () => clearTimeout(t);
  }, [body, subject, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Macro insert ----
  const insertMacro = (macroText, opts = {}) => {
    if (!editor) return;
    // Snippets may contain HTML (<br>, <strong>) – insert as HTML so they render.
    if (opts.asHtml || macroText.includes("<")) {
      editor.chain().focus().insertContent(macroText, { parseOptions: { preserveWhitespace: "full" } }).run();
    } else {
      editor.chain().focus().insertContent(macroText).run();
    }
  };
  const insertSubjectMacro = (macroText) => {
    setSubject((s) => (s ? s + " " + macroText : macroText));
    setDirty(true);
  };
  const insertSubSubjectMacro = (macroText) => {
    setSubSubject((s) => (s ? s + " " + macroText : macroText));
    setDirty(true);
  };

  const setLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link-URL (leer = entfernen):", prev);
    if (url === null) return;
    if (!url) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertTable = () => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  // ---- Export ----
  const handleExport = async (format) => {
    if (dirty) await performSave(true);
    // Phase 21β-3.2 — Wenn der Nutzer die Vorschau bearbeitet hat, exportieren
    // wir den bearbeiteten Inhalt (Macros sind bereits aufgelöst). Außerdem
    // wird dieser bearbeitete Stand als Version gespeichert (siehe Versionen).
    const usingEditedPreview = livePreview && previewDirty && previewEditor;
    const exportBody = usingEditedPreview
      ? previewEditor.getHTML()
      : (editor?.getHTML() || body);
    try {
      const res = await fetch(`${API}/templates/${id}/render?format=${format}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          tenancy_id: previewTenancyId || undefined,
          nk_billing_id: previewBillingId || undefined,
          override_body: exportBody,
          override_subject: subject,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const dispo = res.headers.get("Content-Disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(dispo);
      const filename = m ? m[1] : `${name || "Vorlage"}.${format}`;
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(u);
      toast.success(`${format.toUpperCase()} heruntergeladen`);

      // Bearbeitete Vorschau → als Version speichern (auf Wunsch des Users).
      if (usingEditedPreview) {
        try {
          await api.post(`/templates/${id}/versions`, {
            subject, body: exportBody,
            reason: `preview_export_${format}`,
          });
          setPreviewDirty(false);
          toast.success("Bearbeitete Vorschau als Version gespeichert", { duration: 4000 });
        } catch (verr) {
          // Export ist bereits erfolgt — Versions-Snapshot war optional.
          // Der Nutzer soll trotzdem wissen, dass die Versionierung fehlschlug.
          toast.warning("Export war erfolgreich, aber die Vorschau-Version konnte nicht gespeichert werden.");
          // eslint-disable-next-line no-console
          console.warn("[template-version-snapshot]", verr);
        }
      }
    } catch (e) {
      toast.error(e.message || String(e));
    }
  };

  // ---- Filtered macros ----
  const macroSections = useMemo(() => {
    const q = macroSearch.trim().toLowerCase();
    return Object.entries(macros).map(([section, items]) => {
      const filtered = items.filter((m) => {
        if (!q) return true;
        return (m.key + " " + (m.label || "") + " " + (m.example || "")).toLowerCase().includes(q);
      });
      return [section, filtered];
    }).filter(([, items]) => items.length > 0);
  }, [macros, macroSearch]);

  if (!tpl) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center text-sm text-slate-500">
        Lade Vorlage…
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-slate-100" data-testid="template-editor-page">
      {/* ---------- TOPBAR ---------- */}
      <header className="bg-white border-b border-slate-200 px-3 lg:px-5 py-2 flex items-center gap-2 shrink-0" data-testid="editor-topbar">
        <Button variant="ghost" size="sm" onClick={() => navigate("/vorlagen")} className="gap-1.5" data-testid="editor-back">
          <CaretLeft size={16} /> <span className="hidden sm:inline">Bibliothek</span>
        </Button>
        <div className="h-5 w-px bg-slate-200 mx-1" />
        <Input
          value={name}
          onChange={(e) => { setName(e.target.value); setDirty(true); }}
          className="h-8 flex-1 min-w-0 max-w-sm border-transparent hover:border-slate-200 focus-visible:border-slate-300 font-medium"
          placeholder="Vorlagenname"
          data-testid="editor-name"
        />
        <div className="hidden sm:flex items-center gap-1 text-[11px] text-slate-500">
          {saving ? (
            <span>Speichert…</span>
          ) : dirty ? (
            <span className="text-amber-600">● Ungespeichert</span>
          ) : savedAt ? (
            <span className="text-emerald-600">✓ Gespeichert · {savedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span>
          ) : null}
        </div>

        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <Button
            variant={livePreview ? "default" : "outline"} size="sm"
            onClick={() => setLivePreview((v) => !v)}
            className={`gap-1.5 ${livePreview ? "bg-indigo-600 hover:bg-indigo-700" : ""}`}
            data-testid="editor-preview-toggle"
            title="Mit Beispieldaten anzeigen"
          >
            {livePreview ? <EyeSlash size={14} /> : <Eye size={14} />}
            <span className="hidden md:inline">{livePreview ? "Bearbeiten" : "Vorschau"}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSnippetPickerOpen(true)}
                  className="gap-1.5" data-testid="editor-snippets">
            <Stack size={14} /> <span className="hidden md:inline">Bausteine</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setVersionsOpen(true)} className="gap-1.5" data-testid="editor-versions">
            <ClockCounterClockwise size={14} /> <span className="hidden md:inline">Versionen</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("pdf")} className="gap-1.5 text-rose-700 border-rose-200" data-testid="editor-export-pdf">
            <FilePdf size={14} /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("docx")} className="gap-1.5 text-indigo-700 border-indigo-200" data-testid="editor-export-docx">
            <FileDoc size={14} /> DOCX
          </Button>
          <Button size="sm" onClick={() => performSave(false)} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="editor-save">
            <FloppyDisk size={14} /> Speichern
          </Button>
        </div>
      </header>

      {/* ---------- WORD RIBBON (Start · Einfügen · Layout) ---------- */}
      <WordRibbon
        editor={livePreview ? previewEditor : editor}
        layout={{
          marginTop, marginBottom, marginLeft, marginRight,
          orientation: pageOrientation,
          pageFormat,
          columns: pageColumns,
          hyphenation,
        }}
        onLayoutChange={(patch) => {
          if (patch.marginTop !== undefined) setMarginTop(patch.marginTop);
          if (patch.marginBottom !== undefined) setMarginBottom(patch.marginBottom);
          if (patch.marginLeft !== undefined) setMarginLeft(patch.marginLeft);
          if (patch.marginRight !== undefined) setMarginRight(patch.marginRight);
          if (patch.orientation !== undefined) setPageOrientation(patch.orientation);
          if (patch.pageFormat !== undefined) setPageFormat(patch.pageFormat);
          if (patch.columns !== undefined) setPageColumns(patch.columns);
          if (patch.hyphenation !== undefined) setHyphenation(patch.hyphenation);
          setDirty(true);
        }}
      />

      {/* ---------- BODY ---------- */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[220px_1fr_340px]">
        {/* Settings sidebar */}
        <aside className="hidden lg:flex flex-col border-r border-slate-200 bg-white overflow-y-auto p-3 gap-3" data-testid="editor-settings">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Kategorie</Label>
            <CategorySelect
              value={category}
              onChange={(v) => { setCategory(v); setDirty(true); }}
              testid="editor-category"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Betreff</Label>
            <textarea value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }}
                   className="w-full mt-1 border border-slate-200 rounded-sm p-2 text-sm resize-none" rows={2}
                   placeholder="z. B. Mahnung – {{tenancy.contract_number}}"
                   data-testid="editor-subject" />
            {previewedSubject && previewedSubject !== subject && (
              <div className="mt-1 px-2 py-1 bg-emerald-50/60 border-l-2 border-emerald-300 rounded-sm text-[11px] text-slate-700"
                   data-testid="editor-subject-preview"
                   title="So sieht der Betreff mit ersetzten Makros aus">
                <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold">Vorschau</span>
                <div className="leading-snug">{previewedSubject}</div>
              </div>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Unterbetreff</Label>
            <textarea value={subSubject} onChange={(e) => { setSubSubject(e.target.value); setDirty(true); }}
                   className="w-full mt-1 border border-slate-200 rounded-sm p-2 text-xs resize-none" rows={2}
                   placeholder="z. B. Ihr Mietverhältnis: {{tenancy.contract_number}}"
                   data-testid="editor-sub-subject" />
            <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
              Wird kleiner unter dem Betreff gedruckt. Platzhalter erlaubt.
            </p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Interne Beschreibung</Label>
            <textarea value={description} onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
                      className="w-full mt-1 border border-slate-200 rounded-sm p-2 text-xs resize-none" rows={3}
                      placeholder="Wofür wird diese Vorlage genutzt?"
                      data-testid="editor-description" />
          </div>
          <div className="pt-2 border-t border-slate-100 space-y-2" data-testid="editor-layout-settings">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Layout</Label>
            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={useLetterhead}
                onChange={(e) => { setUseLetterhead(e.target.checked); setDirty(true); }}
                className="mt-0.5 accent-slate-900"
                data-testid="editor-use-letterhead"
              />
              <span>
                <span className="font-medium">Mit Briefpapier</span>
                <span className="block text-[10px] text-slate-500 mt-0.5">
                  Logo, Absender, Empfänger-Adresse, Footer (für Briefe nötig).
                </span>
              </span>
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <Label className="text-[9px] uppercase tracking-wider text-slate-500">Zeilenabstand</Label>
                <select
                  value={lineHeight}
                  onChange={(e) => { setLineHeight(Number(e.target.value)); setDirty(true); }}
                  className="w-full h-7 mt-0.5 border border-slate-200 rounded-sm px-1 text-[11px] bg-white"
                  data-testid="editor-line-height"
                >
                  <option value={1.0}>1,0</option>
                  <option value={1.15}>1,15</option>
                  <option value={1.5}>1,5</option>
                  <option value={2.0}>2,0</option>
                </select>
              </div>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Seitenränder (mm)
              </summary>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                <NumInput label="Oben" value={marginTop} setValue={(v) => { setMarginTop(v); setDirty(true); }} testid="editor-margin-top" />
                <NumInput label="Unten" value={marginBottom} setValue={(v) => { setMarginBottom(v); setDirty(true); }} testid="editor-margin-bottom" />
                <NumInput label="Links" value={marginLeft} setValue={(v) => { setMarginLeft(v); setDirty(true); }} testid="editor-margin-left" />
                <NumInput label="Rechts" value={marginRight} setValue={(v) => { setMarginRight(v); setDirty(true); }} testid="editor-margin-right" />
              </div>
            </details>
          </div>
          <div className="pt-2 border-t border-slate-100">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Test-Mietverhältnis</Label>
            <select value={previewTenancyId} onChange={(e) => setPreviewTenancyId(e.target.value)}
                    className="w-full h-8 mt-1 border border-slate-200 rounded-sm px-2 text-xs bg-white"
                    data-testid="editor-test-tenancy">
              <option value="">(ohne)</option>
              {tenancies.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.contract_number || t.id.slice(0, 8)} · {t.tenant_name || t.tenant_names || ""}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
              Für „Beispieldaten" und Export. Wird nicht gespeichert.
            </p>
          </div>
          {billings.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Test-NK-Abrechnung</Label>
              <select value={previewBillingId} onChange={(e) => setPreviewBillingId(e.target.value)}
                      className="w-full h-8 mt-1 border border-slate-200 rounded-sm px-2 text-xs bg-white"
                      data-testid="editor-test-nk">
                <option value="">(ohne — Tabelle bleibt leer)</option>
                {billings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title || b.id.slice(0, 8)} · {(b.period_from || "").slice(0, 10)}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                Aktiviert die NK-Makros (<code>{`{{nk.table}}`}</code>, <code>{`{{nk.balance_label}}`}</code>).
              </p>
            </div>
          )}
          {tpl.is_default && (
            <div className="mt-auto bg-emerald-50 border border-emerald-200 rounded-sm p-2 text-[10px] text-emerald-800">
              <strong>Standard-Vorlage:</strong> Änderungen wirken sich auf alle Schriften aus, die diese Vorlage nutzen.
            </div>
          )}
        </aside>

        {/* A4 page */}
        <main className="overflow-y-auto p-4 lg:p-8" data-testid="editor-canvas">
          <div className="mx-auto max-w-[794px]">
            {/* Mobile category/subject */}
            <div className="lg:hidden bg-white border border-slate-200 rounded-md p-3 mb-3 space-y-2">
              <CategorySelect value={category}
                              onChange={(v) => { setCategory(v); setDirty(true); }}
                              testid="editor-category-mobile" />
              <Input value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }}
                     placeholder="Betreff" className="h-9" />
            </div>

            {/* The page itself — applies user-chosen margins (mm→px ~3.78) + font */}
            <div
              className="relative bg-white border border-slate-200 shadow-sm rounded-md min-h-[1000px]"
              style={{
                paddingTop: `${marginTop * 3.78}px`,
                paddingBottom: `${marginBottom * 3.78}px`,
                paddingLeft: `${marginLeft * 3.78}px`,
                paddingRight: `${marginRight * 3.78}px`,
                "--tt-font-family": `${fontFamily}, "Segoe UI", Helvetica, Arial, sans-serif`,
                "--tt-font-size": `${fontSizePt}pt`,
                "--tt-line-height": String(lineHeight),
              }}
              data-testid="editor-page"
            >
              {/* A4 page-break indicators (every ~1056px content height) */}
              <div className="pointer-events-none absolute inset-x-0 top-[1056px] bottom-0 hidden lg:block" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i}
                       className="absolute left-0 right-0 border-t border-dashed border-slate-300"
                       style={{ top: `${i * 1056}px` }}>
                    <span className="absolute -top-2.5 right-0 text-[9px] text-slate-400 bg-white px-1 rounded-sm border border-slate-200">
                      Seite {i + 2}
                    </span>
                  </div>
                ))}
              </div>
              {/* Letterhead-preview (only when use_letterhead = true, non-editable) */}
              {useLetterhead && !livePreview && (
                <div className="tiptap-letterhead-preview" data-testid="editor-letterhead-preview">
                  <div className="lh-company">Briefkopf wird beim Export aus Stammdaten ergänzt</div>
                  <div>Logo · Absender · Empfänger-Adresse · Footer</div>
                </div>
              )}
              {livePreview ? (
                <div data-testid="editor-live-preview">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Betreff</div>
                  <div className="text-base font-semibold text-slate-900 mb-4 break-words">{previewedSubject || "—"}</div>
                  <EditorContent editor={previewEditor} />
                  {previewDirty && (
                    <div className="mt-3 px-3 py-2 bg-amber-50 border-l-4 border-amber-400 rounded-sm text-[11px] text-slate-700"
                         data-testid="preview-dirty-warning">
                      <strong>Vorschau bearbeitet.</strong> Diese Änderungen werden{" "}
                      <em>nicht</em> in die Vorlage zurückgeschrieben — sonst gingen die
                      Platzhalter verloren. Beim Export/Versand wird die bearbeitete
                      Vorschau verwendet.
                    </div>
                  )}
                </div>
              ) : (
                <EditorContent editor={editor} data-testid="editor-tiptap" />
              )}
            </div>
          </div>
        </main>

        {/* Macro panel */}
        <aside className="hidden lg:flex flex-col border-l border-slate-200 bg-white overflow-hidden" data-testid="editor-macros">
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-sm font-semibold inline-flex items-center gap-1.5 mb-2">
              <Sparkle size={14} className="text-amber-500" /> Platzhalter
            </div>
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={macroSearch}
                onChange={(e) => setMacroSearch(e.target.value)}
                placeholder="Suche nach Platzhalter…"
                className="h-8 pl-7 text-xs"
                data-testid="macro-search"
              />
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">
              Klick → in Text einfügen. „→ Betreff" fügt in den Betreff ein.
            </p>
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-3">
            {macroSections.length === 0 && (
              <div className="text-xs text-slate-500 text-center py-6">Keine Treffer</div>
            )}
            {macroSections.map(([section, items]) => (
              <div key={section}>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 px-1.5 mb-1 flex items-center gap-1">
                  {SECTION_LABEL[section] || section}
                  {section === "snippet" && (
                    <span className="ml-1 px-1 py-0.5 bg-violet-50 border border-violet-200 text-violet-700 rounded-sm text-[8px] font-medium normal-case tracking-normal">
                      Bedingt & Mehrzeilig
                    </span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {items.map((m) => (
                    section === "snippet" ? (
                      <SnippetRow
                        key={m.key}
                        snippet={m}
                        onInsert={() => insertMacro(m.macro, { asHtml: true })}
                      />
                    ) : (
                      <MacroRow
                        key={m.key}
                        macro={m}
                        onInsertBody={() => insertMacro(m.macro)}
                        onInsertSubject={() => insertSubjectMacro(m.macro)}
                        onInsertSubSubject={() => insertSubSubjectMacro(m.macro)}
                      />
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {/* Snippet picker dialog */}
      <SnippetPicker
        open={snippetPickerOpen}
        onOpenChange={setSnippetPickerOpen}
        currentTemplateId={id}
        onInsert={(html) => {
          const active = livePreview ? previewEditor : editor;
          if (active) active.chain().focus().insertContent(html).run();
        }}
      />

      {/* Versions dialog */}
      {versionsOpen && (
        <VersionsDialog
          templateId={id}
          onClose={() => setVersionsOpen(false)}
          onRestored={async () => {
            const { data } = await api.get(`/templates/${id}`);
            setTpl(data);
            setName(data.name || ""); setCategory(data.category || "");
            setSubject(data.subject || ""); setBody(data.body || "<p></p>");
            if (editor) editor.commands.setContent(data.body || "<p></p>", false);
            setDirty(false);
          }}
        />
      )}

      {/* KI-Improve dialog */}
      {aiImproveOpen && (
        <AiImproveDialog
          currentSubject={subject}
          currentBody={editor?.getHTML() || body}
          onClose={() => setAiImproveOpen(false)}
          onApply={(newSubject, newBody) => {
            setSubject(newSubject || subject);
            if (editor && newBody) editor.commands.setContent(newBody, false);
            setBody(newBody || body);
            setDirty(true);
            setAiImproveOpen(false);
            toast.success("KI-Verbesserung übernommen");
          }}
        />
      )}
    </div>
  );
}

function AiImproveDialog({ currentSubject, currentBody, onClose, onApply }) {
  const [goal, setGoal] = useState("");
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!goal.trim()) { toast.error("Bitte Ziel eingeben"); return; }
    setWorking(true);
    try {
      const { data } = await api.post("/templates/ai/improve", {
        current_subject: currentSubject || "",
        current_body: currentBody || "",
        goal,
      });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWorking(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="ai-improve-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <MagicWand size={20} className="text-violet-600" /> Vorlage mit KI verbessern
          </DialogTitle>
          <DialogDescription>
            Beschreibe in einem Satz, wie die KI deine aktuelle Vorlage anpassen soll. Platzhalter bleiben erhalten.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Was soll die KI ändern?</Label>
              <textarea
                value={goal} onChange={(e) => setGoal(e.target.value)}
                rows={3}
                placeholder={'z. B. „freundlicher formulieren", „kürzer", „juristisch präziser machen", „Frist auf 7 Tage verkürzen"'}
                className="w-full mt-1 border border-slate-300 rounded-sm p-2 text-sm resize-none"
                data-testid="ai-improve-goal"
              />
            </div>
            <div className="text-[11px] text-slate-500">
              Beispiele: <em>„Formell mit Sie-Form"</em> · <em>„Mit Inkasso-Hinweis ergänzen"</em> · <em>„Verständlicher für Mieter"</em>
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="ai-improve-preview">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Neuer Betreff</Label>
              <div className="bg-violet-50 border border-violet-200 rounded-sm p-2 text-sm font-medium">{result.subject}</div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Neuer Inhalt</Label>
              <div className="bg-white border border-violet-200 rounded-sm p-3 tiptap-doc text-xs"
                   dangerouslySetInnerHTML={{ __html: result.body }} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={working}>Abbrechen</Button>
          {!result ? (
            <Button onClick={submit} disabled={working} className="bg-violet-600 hover:bg-violet-700 gap-1.5" data-testid="ai-improve-submit">
              <MagicWand size={14} /> {working ? "KI arbeitet…" : "Verbesserungsvorschlag erzeugen"}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setResult(null)}>Erneut versuchen</Button>
              <Button onClick={() => onApply(result.subject, result.body)}
                      className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
                      data-testid="ai-improve-apply">
                Übernehmen
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------- Macro Row -----------------------
function MacroRow({ macro, onInsertBody, onInsertSubject, onInsertSubSubject }) {
  return (
    <div className="group flex items-center gap-1 px-1.5 py-1 hover:bg-slate-50 rounded-sm cursor-default">
      <button
        type="button"
        onClick={onInsertBody}
        title={`Beispiel: ${macro.example || "—"}\nKlick: in Text einfügen`}
        data-testid={`macro-insert-${macro.key}`}
        className="flex-1 text-left min-w-0"
      >
        <div className="text-xs text-slate-800 font-medium truncate">{macro.label}</div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-slate-400 truncate">{macro.key}</span>
          {macro.example && (
            <span className="text-[10px] text-emerald-700 truncate">→ {macro.example}</span>
          )}
        </div>
      </button>
      <button
        type="button"
        onClick={onInsertSubject}
        title="In Betreff einfügen"
        data-testid={`macro-subject-${macro.key}`}
        className="opacity-0 group-hover:opacity-100 text-[10px] font-medium px-1.5 py-1 text-indigo-600 hover:bg-indigo-50 rounded-sm shrink-0"
      >
        → Betreff
      </button>
      {onInsertSubSubject && (
        <button
          type="button"
          onClick={onInsertSubSubject}
          title="In Unterbetreff einfügen"
          data-testid={`macro-subsubject-${macro.key}`}
          className="opacity-0 group-hover:opacity-100 text-[10px] font-medium px-1.5 py-1 text-emerald-600 hover:bg-emerald-50 rounded-sm shrink-0"
        >
          → Unterbetreff
        </button>
      )}
    </div>
  );
}

// ----------------------- Snippet Row -----------------------
function SnippetRow({ snippet, onInsert }) {
  return (
    <button
      type="button"
      onClick={onInsert}
      title={`${snippet.description || ""}\n\nVorschau: ${snippet.example || ""}`}
      data-testid={`macro-insert-${snippet.key}`}
      className="w-full text-left px-2 py-1.5 hover:bg-violet-50 border border-transparent hover:border-violet-200 rounded-sm transition-colors"
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        <Sparkle size={10} weight="duotone" className="text-violet-500 shrink-0" />
        <span className="text-xs font-medium text-slate-800 truncate">{snippet.label}</span>
      </div>
      {snippet.example && (
        <div className="text-[10px] text-emerald-700 truncate pl-4">→ {snippet.example}</div>
      )}
      {snippet.description && (
        <div className="text-[10px] text-slate-500 leading-snug pl-4 line-clamp-2">{snippet.description}</div>
      )}
    </button>
  );
}

// ----------------------- Toolbar -----------------------
function Toolbar({ editor, onSetLink, onInsertTable, fontFamily, setFontFamily, fontSizePt, setFontSizePt }) {
  if (!editor) return null;
  const btn = (active, onClick, Icon, testid, title) => (
    <button
      type="button" title={title} onClick={onClick} data-testid={testid}
      className={`h-7 w-7 inline-flex items-center justify-center rounded-sm border ${
        active ? "bg-slate-900 text-white border-slate-900" : "border-transparent hover:bg-slate-100 text-slate-700"
      }`}
    >
      <Icon size={13} />
    </button>
  );
  const Sep = () => <div className="w-px h-4 bg-slate-200 mx-0.5" />;
  return (
    <div className="bg-white border-b border-slate-200 px-2 py-1 flex items-center gap-0.5 overflow-x-auto shrink-0" data-testid="editor-toolbar">
      {/* Font family + size inline (Word-Bar) */}
      <select
        value={fontFamily}
        onChange={(e) => setFontFamily(e.target.value)}
        className="h-7 border border-slate-200 rounded-sm px-1.5 text-[11px] bg-white"
        data-testid="fmt-font-family"
        title="Schriftart"
      >
        {["Calibri","Arial","Times New Roman","Courier New"].map((f) => (
          <option key={f} value={f}>{f}</option>
        ))}
      </select>
      <select
        value={fontSizePt}
        onChange={(e) => setFontSizePt(Number(e.target.value))}
        className="h-7 border border-slate-200 rounded-sm px-1 text-[11px] bg-white w-14"
        data-testid="fmt-font-size"
        title="Schriftgröße"
      >
        {[8,9,10,11,12,14,16,18,24,32].map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <Sep />
      {btn(editor.isActive("heading", { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), TextHOne, "fmt-h1", "Überschrift 1")}
      {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), TextHTwo, "fmt-h2", "Überschrift 2")}
      {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), TextHThree, "fmt-h3", "Überschrift 3")}
      <Sep />
      {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), TextB, "fmt-bold", "Fett (Ctrl+B)")}
      {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), TextItalic, "fmt-italic", "Kursiv (Ctrl+I)")}
      {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), TextUnderline, "fmt-underline", "Unterstrichen (Ctrl+U)")}
      {btn(editor.isActive("strike"), () => editor.chain().focus().toggleStrike().run(), TextStrikethrough, "fmt-strike", "Durchgestrichen")}
      <Sep />
      {/* color picker */}
      <ColorPicker editor={editor} />
      <Sep />
      {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), ListBullets, "fmt-ul", "Aufzählung")}
      {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListNumbers, "fmt-ol", "Nummerierung")}
      <Sep />
      {btn(editor.isActive({ textAlign: "left" }), () => editor.chain().focus().setTextAlign("left").run(), TextAlignLeft, "fmt-l", "Linksbündig")}
      {btn(editor.isActive({ textAlign: "center" }), () => editor.chain().focus().setTextAlign("center").run(), TextAlignCenter, "fmt-c", "Zentriert")}
      {btn(editor.isActive({ textAlign: "right" }), () => editor.chain().focus().setTextAlign("right").run(), TextAlignRight, "fmt-r", "Rechtsbündig")}
      {btn(editor.isActive({ textAlign: "justify" }), () => editor.chain().focus().setTextAlign("justify").run(), TextAlignJustify, "fmt-j", "Blocksatz")}
      <Sep />
      {btn(editor.isActive("link"), onSetLink, LinkIcon, "fmt-link", "Link einfügen / entfernen")}
      <HighlightPicker editor={editor} />
      <TableMenu editor={editor} onInsert={onInsertTable} />
      {btn(false, () => editor.chain().focus().setHorizontalRule().run(), Minus, "fmt-hr", "Trennlinie")}
      <Sep />
      {btn(false, () => editor.chain().focus().undo().run(), ArrowUUpLeft, "fmt-undo", "Rückgängig (Ctrl+Z)")}
      {btn(false, () => editor.chain().focus().redo().run(), ArrowUUpRight, "fmt-redo", "Wiederherstellen")}
    </div>
  );
}

function ColorPicker({ editor }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-700 border border-transparent" title="Textfarbe" data-testid="fmt-color">
          <div className="w-3.5 h-3.5 rounded-sm border border-slate-300" style={{ background: editor.getAttributes("textStyle").color || "#0f172a" }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44 p-2">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Textfarbe</DropdownMenuLabel>
        <div className="grid grid-cols-4 gap-1 mt-1">
          {FONT_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => editor.chain().focus().setColor(c).run()}
              className="w-8 h-8 rounded-sm border border-slate-200 hover:scale-110 transition-transform"
              style={{ background: c }}
              data-testid={`color-${c.replace("#", "")}`}
              title={c}
            />
          ))}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => editor.chain().focus().unsetColor().run()}>
          Farbe zurücksetzen
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HighlightPicker({ editor }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`h-7 w-7 inline-flex items-center justify-center rounded-sm border ${
            editor.isActive("highlight") ? "bg-slate-900 text-white border-slate-900" : "border-transparent hover:bg-slate-100 text-slate-700"
          }`}
          title="Texthervorhebung (Marker)"
          data-testid="fmt-highlight"
        >
          <Highlighter size={13} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44 p-2">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Texthervorhebung</DropdownMenuLabel>
        <div className="grid grid-cols-3 gap-1 mt-1">
          {HIGHLIGHT_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => editor.chain().focus().toggleHighlight({ color: c }).run()}
              className="w-10 h-8 rounded-sm border border-slate-200 hover:scale-105 transition-transform"
              style={{ background: c }}
              data-testid={`highlight-${c.replace("#", "")}`}
              title={c}
            />
          ))}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => editor.chain().focus().unsetHighlight().run()}>
          Hervorhebung entfernen
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NumInput({ label, value, setValue, testid }) {
  return (
    <label className="flex flex-col">
      <span className="text-[9px] text-slate-500">{label}</span>
      <input
        type="number"
        min={0}
        max={80}
        value={value}
        onChange={(e) => setValue(Number(e.target.value) || 0)}
        className="h-7 mt-0.5 border border-slate-200 rounded-sm px-1.5 text-[11px] bg-white"
        data-testid={testid}
      />
    </label>
  );
}

function TableMenu({ editor, onInsert }) {
  const inTable = editor.isActive("table");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`h-7 w-7 inline-flex items-center justify-center rounded-sm border ${
            inTable ? "bg-slate-900 text-white border-slate-900" : "border-transparent hover:bg-slate-100 text-slate-700"
          }`}
          title="Tabelle"
          data-testid="fmt-table"
        >
          <TableIcon size={13} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuItem onClick={onInsert}>Tabelle 3×3 einfügen</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => editor.chain().focus().addRowAfter().run()} disabled={!inTable}>Zeile darunter</DropdownMenuItem>
        <DropdownMenuItem onClick={() => editor.chain().focus().addRowBefore().run()} disabled={!inTable}>Zeile darüber</DropdownMenuItem>
        <DropdownMenuItem onClick={() => editor.chain().focus().addColumnAfter().run()} disabled={!inTable}>Spalte rechts</DropdownMenuItem>
        <DropdownMenuItem onClick={() => editor.chain().focus().addColumnBefore().run()} disabled={!inTable}>Spalte links</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => editor.chain().focus().mergeOrSplit().run()} disabled={!inTable}>Zellen verbinden / teilen</DropdownMenuItem>
        <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeaderRow().run()} disabled={!inTable}>Kopfzeile umschalten</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => editor.chain().focus().deleteRow().run()} disabled={!inTable} className="text-rose-700">Zeile löschen</DropdownMenuItem>
        <DropdownMenuItem onClick={() => editor.chain().focus().deleteColumn().run()} disabled={!inTable} className="text-rose-700">Spalte löschen</DropdownMenuItem>
        <DropdownMenuItem onClick={() => editor.chain().focus().deleteTable().run()} disabled={!inTable} className="text-rose-700">Tabelle löschen</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ----------------------- Versions dialog -----------------------
function VersionsDialog({ templateId, onClose, onRestored }) {
  const [items, setItems] = useState(null);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/templates/${templateId}/versions`);
        setItems(data || []);
      } catch (e) {
        toast.error(formatApiError(e));
        setItems([]);
      }
    })();
  }, [templateId]);

  const restore = async (ver) => {
    if (!await window._domivusConfirm(`Diese Version vom ${new Date(ver.created_at).toLocaleString("de-DE")} wirklich wiederherstellen? Der aktuelle Stand wird als neue Version gesichert.`)) return;
    setRestoring(ver.id);
    try {
      await api.post(`/templates/${templateId}/versions/${ver.id}/restore`);
      toast.success("Version wiederhergestellt");
      await onRestored();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="versions-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <ClockCounterClockwise size={20} className="text-indigo-600" /> Versionsverlauf
          </DialogTitle>
          <DialogDescription>
            Jeder Speichervorgang erzeugt eine Sicherung. Klick auf „Wiederherstellen" lädt die alte Version zurück (Aktueller Stand wird zusätzlich gesichert).
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
          {items === null && <div className="text-sm text-slate-500 py-6 text-center">Lade…</div>}
          {items && items.length === 0 && (
            <div className="text-sm text-slate-500 py-6 text-center">
              Noch keine Versionen vorhanden. Sobald du die Vorlage bearbeitest, wird der vorherige Stand hier gesichert.
            </div>
          )}
          <ul className="space-y-2">
            {items?.map((v) => (
              <li key={v.id} className="bg-slate-50 border border-slate-200 rounded-md p-3 flex items-start gap-3" data-testid={`version-row-${v.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500 mb-1">
                    {new Date(v.created_at).toLocaleString("de-DE")} · {v.author || "—"}
                    {v.reason === "pre-restore" && (
                      <span className="ml-1.5 inline-block text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm">vor Restore</span>
                    )}
                  </div>
                  <div className="text-sm font-medium text-slate-900 mb-1 truncate">{v.subject}</div>
                  <div className="text-[11px] text-slate-500 line-clamp-2" dangerouslySetInnerHTML={{
                    __html: (v.body || "").slice(0, 280),
                  }} />
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => restore(v)}
                  disabled={restoring === v.id}
                  className="gap-1.5 shrink-0"
                  data-testid={`version-restore-${v.id}`}
                >
                  <ArrowCounterClockwise size={12} /> Wiederherstellen
                </Button>
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
